import { supabase } from '../supabase.js';
import { DOC_KINDS, DOC_LABELS, MEMO_FILES, WARNING_TEXT, withDefaults, monthsBetween, fmtShort, todayIso } from '../lib/learner-docs-core.mjs';
import { loadCohorts, buildLearnerDocs, downloadPdf, slug } from '../lib/learner-docs.js';

/* ============================================================
   Payslips & documents › Document settings (docs/learner-documents.md).
   Per cohort: what the confirmation letter, salary schedule and
   UI-19 print, and which classes belong to it. Below the cohorts,
   period corrections: a learner whose dates on the documents must
   differ from their class and their own start / exit dates.
   Admins change; other learner-pay holders read and preview.
============================================================ */

const TERMINATION_CODES = [
  ['2', 'Deceased'], ['3', 'Retired'], ['4', 'Dismissed'], ['5', 'Contract expired'], ['6', 'Resigned'],
  ['7', 'Constructive dismissal'], ['8', 'Insolvency / liquidation'], ['9', 'Maternity / adoption'],
  ['10', 'Illness / medically boarded'], ['11', 'Retrenched / staff reduction'], ['12', 'Transfer to another branch'],
  ['13', 'Absconded'], ['14', 'Business closed'], ['15', 'Death of domestic employer'],
  ['16', 'Voluntary severance package'], ['17', 'Reduced work time'], ['18', 'Commissioning parental'], ['19', 'Parental leave'],
];
const NON_CONTRIB = [['', 'None'], ['1', '1 · Temporary (under 24 hrs/month)'], ['2', '2 · Commission only'], ['3', '3 · No income paid']];
const SOURCES = [['settings', 'Settings (typed here)'], ['system', 'From the system']];
const AMOUNT_MODES = [['gross', 'Gross: actual stipend before UIF (e.g. 2000)'], ['net', 'Net: actual stipend after UIF (e.g. 1980)'], ['flat', 'Flat: monthly target every month']];
const HOURS_MODES = [['fixed', 'Fixed hours per paid month'], ['per_day', 'Days present × hours per day'], ['days', 'Days present (no hours)']];

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const opts = (list, val) => list.map(([v, l]) => `<option value="${esc(v)}" ${String(v) === String(val ?? '') ? 'selected' : ''}>${esc(l)}</option>`).join('');
const day = (iso) => (iso ? fmtShort(String(iso).slice(0, 10)) : '—');
const rands = (n) => `R${Number(n || 0).toLocaleString('en-ZA', { maximumFractionDigits: 2 })}`;

const S = { cohorts: [], classes: [], open: new Set() };

/** Draw the Document settings tab into `el`. */
export async function renderDocSettings(el) {
  el.innerHTML = `<p class="ds-hint" style="padding:16px 0">Loading…</p>`;
  await loadBase();
  draw(el);
}

async function loadBase() {
  const [cohorts, classes] = await Promise.all([
    loadCohorts(),
    supabase.from('classes')
      .select('class_id,class_name,cohort_id,start_date,end_date,is_test,programmes(programme_name),training_venues(towns(municipalities(municipality_name)))')
      .order('class_name'),
  ]);
  if (classes.error) throw new Error(classes.error.message);
  const def = cohorts.find((k) => k.is_default)?.cohort_id ?? null;
  S.cohorts = cohorts;
  S.classes = (classes.data || []).map((c) => ({
    class_id: c.class_id, class_name: c.class_name, is_test: c.is_test === true,
    cohort_id: c.cohort_id ?? def, cohort_set: c.cohort_id != null,
    start_date: c.start_date, end_date: c.end_date,
    programme: c.programmes?.programme_name || '',
    muni: c.training_venues?.towns?.municipalities?.municipality_name || '',
  }));
}

const canEdit = () => window.__access?.admin === true;   // owner or admin, as the cohorts policy

function draw(el) {
  const edit = canEdit();
  el.innerHTML = `
    <style>
      .ds-bar { display:flex; flex-wrap:wrap; gap:8px 12px; align-items:center; padding:12px 16px; margin-bottom:14px; border:1px solid var(--border); border-radius:10px; background:var(--surface); }
      .ds-bar input, .ds-bar select, .ds-card input, .ds-card select { padding:7px 9px; border:1px solid var(--border); border-radius:6px; background:var(--surface); color:var(--text); font-size:.85rem; font-family:inherit; min-width:0; }
      .ds-card { background:var(--surface); border:1px solid var(--border); border-radius:12px; margin-bottom:12px; }
      .ds-card[open] { border-color:var(--orange); }
      .ds-card > summary { display:flex; flex-wrap:wrap; gap:6px 16px; align-items:center; padding:14px 18px; cursor:pointer; list-style:none; font-size:.84rem; color:var(--text-muted); }
      .ds-card > summary::-webkit-details-marker { display:none; }
      .ds-card > summary::before { content:'▸'; color:var(--text); }
      .ds-card[open] > summary::before { content:'▾'; }
      .ds-card > summary b { color:var(--text); font-size:.98rem; }
      .ds-body { padding:4px 18px 18px; border-top:1px solid var(--border); }
      .ds-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(190px,1fr)); gap:10px 14px; }
      .ds-grid label { display:flex; flex-direction:column; gap:3px; font-size:.74rem; font-weight:600; color:var(--text-muted); }
      .ds-card input:disabled, .ds-card select:disabled { opacity:.6; cursor:not-allowed; }
      .ds-card h3 { margin:18px 0 8px; font-size:.78rem; text-transform:uppercase; letter-spacing:.04em; color:var(--text); }
      .ds-hint { font-size:.76rem; color:var(--text-muted); font-weight:400; }
      .ds-sys-note { font-size:.76rem; color:var(--orange-dark); margin:4px 0 0; }
      .ds-table { width:100%; border-collapse:collapse; font-size:.82rem; }
      .ds-table th { text-align:left; font-size:.72rem; color:var(--text-muted); font-weight:600; padding:4px 6px; border-bottom:1px solid var(--border); }
      .ds-table td { padding:3px 6px; border-bottom:1px solid var(--border-light, var(--border)); }
      .ds-table input { width:100%; padding:5px 7px; }
      .ds-wrap { overflow-x:auto; }
      .ds-classes { max-height:220px; overflow:auto; border:1px solid var(--border); border-radius:8px; padding:6px 10px; font-size:.8rem; }
      .ds-classes label { display:flex; gap:8px; align-items:baseline; padding:3px 0; font-weight:400; color:var(--text); }
      .ds-adv { margin-top:18px; border-top:1px solid var(--border); padding-top:10px; }
      .ds-adv > summary { cursor:pointer; font-size:.84rem; font-weight:600; color:var(--text); }
      .ds-actions { display:flex; flex-wrap:wrap; align-items:center; gap:8px; margin-top:16px; padding-top:14px; border-top:1px solid var(--border); }
      .ds-msg { font-size:.8rem; margin:6px 0 0; }
      .ds-msg.ok { color:#15803D; } .ds-msg.err { color:var(--red); } .ds-msg.warn { color:var(--orange-dark); }
      .ds-section { background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:16px 18px; margin-top:20px; }
      .ds-section h2 { margin:0 0 4px; font-size:1rem; }
    </style>

    <div class="ds-bar">
      <b style="font-size:.9rem">Preview a learner's documents</b>
      <input id="ds-pv-id" placeholder="Learner ID number" inputmode="numeric" aria-label="Learner ID number" style="width:200px">
      <select id="ds-pv-kind" aria-label="Which documents">
        <option value="">All of them (one PDF)</option>
        ${DOC_KINDS.map((x) => `<option value="${x}">${esc(DOC_LABELS[x])}</option>`).join('')}
      </select>
      <button class="btn btn-ghost" id="ds-pv">Preview PDF</button>
      <span style="flex:1"></span>
      ${edit ? `<input id="ds-new-name" placeholder="New cohort name, e.g. July 2026 intake" style="width:240px">
      <button class="btn btn-ghost" id="ds-new">+ New cohort</button>` : ''}
      <p class="ds-msg" id="ds-bar-msg" style="flex-basis:100%;margin:0"></p>
    </div>
    ${edit ? '' : `<p class="ds-hint" style="margin:0 0 12px">Only an admin can change these settings; you can open and preview them.</p>`}

    ${S.cohorts.map((k) => card(k, edit)).join('')}

    <section class="ds-section" id="ds-corr">
      <h2>Period corrections</h2>
      <p class="ds-hint" style="margin:0 0 10px">Only when a learner's dates or cohort on the documents must differ from their class and their own start and exit dates.</p>
      <div id="ds-corr-list"><p class="ds-hint">Loading…</p></div>
    </section>`;

  const barMsg = (t, cls = '') => { const m = el.querySelector('#ds-bar-msg'); m.textContent = t; m.className = `ds-msg ${cls}`; };

  el.querySelector('#ds-pv').addEventListener('click', () => previewById(el, null, barMsg));
  el.querySelector('#ds-new')?.addEventListener('click', async () => {
    const name = el.querySelector('#ds-new-name').value.trim();
    if (!name) { barMsg('Type a name for the new cohort.', 'err'); return; }
    const { error } = await supabase.from('cohorts').insert({ name, config: {} });
    if (error) { barMsg(error.message, 'err'); return; }
    S.cohorts = await loadCohorts();
    draw(el);
  });
  el.querySelectorAll('.ds-card').forEach((d) => d.addEventListener('toggle', () => {
    d.open ? S.open.add(d.dataset.id) : S.open.delete(d.dataset.id);
  }));
  for (const k of S.cohorts) wireCard(el.querySelector(`#ds-card-${k.cohort_id}`), k, edit, el);
  drawCorrections(el.querySelector('#ds-corr-list'), edit);
}

/** Preview by the ID number typed in the bar; `cohortOverride` = a card's unsaved values. */
async function previewById(el, cohortOverride, msg) {
  const id = el.querySelector('#ds-pv-id').value.replace(/\s+/g, '');
  if (!id) { msg('Type a learner ID number in "Preview a learner\'s documents" first.', 'err'); return; }
  msg('Finding the learner…');
  const { data: found, error } = await supabase.from('students').select('student_uuid').eq('national_id', id).limit(2);
  if (error) { msg(error.message, 'err'); return; }
  if (!found?.length) { msg(`No learner has ID number ${id}.`, 'err'); return; }
  if (found.length > 1) { msg(`More than one learner has ID number ${id}; fix the duplicate first.`, 'err'); return; }
  try {
    const kind = el.querySelector('#ds-pv-kind').value;
    const { bytes, docs, skipped } = await buildLearnerDocs([found[0].student_uuid], {
      kinds: kind ? [kind] : undefined, cohortOverride: cohortOverride ?? undefined,
    });
    if (!bytes) { msg(skipped[0]?.reason || 'Nothing to print.', 'err'); return; }
    downloadPdf(bytes, `preview-${slug(docs[0].full_name)}-${kind || 'documents'}.pdf`);
    const w = docs[0].warnings.map((x) => WARNING_TEXT[x] || x);
    msg(`Preview of ${docs[0].full_name} (${day(docs[0].period_start)} – ${day(docs[0].period_end)})${cohortOverride ? ' with the values on screen, not saved' : ''}.${w.length ? ` Note: ${w.join('; ')}.` : ''}`, 'ok');
  } catch (e) {
    msg(`Could not build the preview: ${e.message}`, 'err');
  }
}

function cohortClasses(k) { return S.classes.filter((c) => c.cohort_id === k.cohort_id); }

/** The months a cohort's documents can cover: its classes' dates, else its fallback dates. */
function cohortSpan(k, fallbackStart = k.period_start, fallbackEnd = k.period_end) {
  const cls = cohortClasses(k).filter((c) => c.start_date);
  const start = cls.map((c) => c.start_date).sort()[0] || fallbackStart || null;
  if (!start) return { start: null, end: null };
  const end = cls.some((c) => !c.end_date) ? null
    : (cls.map((c) => c.end_date).sort().at(-1) || fallbackEnd || null);
  return { start, end };
}

function card(k, edit) {
  const c = withDefaults(k.config);
  const dis = edit ? '' : 'disabled';
  const cls = cohortClasses(k);
  const span = cohortSpan(k);
  const money = c.sources.amounts === 'system' ? 'amounts from payslips' : `${rands(c.month_default.salary)} · ${rands(c.month_default.uif)} UIF`;
  const hours = c.sources.hours === 'system' ? 'hours from the system' : `${c.month_default.hours} hrs per month`;
  return `
  <details class="ds-card" id="ds-card-${k.cohort_id}" data-id="${k.cohort_id}" ${S.open.has(String(k.cohort_id)) ? 'open' : ''}>
    <summary>
      <b>${esc(k.name)}</b>${k.is_default ? ' <span class="badge badge-blue">Default</span>' : ''}
      <span>${cls.length} class${cls.length === 1 ? '' : 'es'}</span>
      <span>${span.start ? `${day(span.start)} – ${span.end ? day(span.end) : 'no end date (prints up to today)'}` : 'no dates yet'}</span>
      <span>${esc(money)} · ${esc(hours)}</span>
    </summary>
    <div class="ds-body">
      <h3>What's printed</h3>
      <div class="ds-grid">
        <label>Name <input data-k="name" value="${esc(k.name)}" ${dis}></label>
        <label>Date on the documents <input type="date" data-k="issue_date" value="${esc(c.issue_date || '')}" ${dis}>
          <span class="ds-hint">Blank: the day they are printed</span></label>
        <label>Process memo <select data-k="memo_file" ${dis}>${opts([['', 'None'], ...Object.entries(MEMO_FILES)], c.memo_file || '')}</select>
          <span class="ds-hint">Printed as it is, first in the pack</span></label>
      </div>

      <h3>Salary schedule, month by month</h3>
      <p class="ds-hint" style="margin:0 0 8px">Each learner prints the months inside their own period: a learner who completed gets every month of the cohort; a drop-out's period ends at their exit.</p>
      <div class="ds-wrap">
        <table class="ds-table">
          <thead><tr><th>Month</th><th>Salary (R)</th><th>Hours</th><th>UIF (R)</th><th>Contributor / Non-contributor</th></tr></thead>
          <tbody data-k="schedule"></tbody>
        </table>
      </div>
      <p class="ds-sys-note" data-note="amounts" hidden>Salary and UIF come from the system for each learner; the amounts typed here are ignored.</p>
      <p class="ds-sys-note" data-note="hours" hidden>Hours come from the system for each learner; the hours typed here are ignored.</p>

      <h3>UI-19</h3>
      <div class="ds-grid">
        <label>Remuneration per month (R) <input type="number" step="0.01" min="0" data-k="u_remuneration" value="${esc(c.ui19.remuneration)}" ${dis}></label>
        <label>Hours worked per month <input type="number" step="0.5" min="0" data-k="u_total_hours" value="${esc(c.ui19.total_hours)}" ${dis}>
          <span class="ds-hint" data-k="hours_sum"></span></label>
        <label>Reason for termination <select data-k="u_termination_code" ${dis}>${opts(TERMINATION_CODES.map(([v, l]) => [v, `${v} · ${l}`]), c.ui19.termination_code)}</select></label>
        <label>Contributor <select data-k="u_contributor" ${dis}>${opts([['YES', 'YES'], ['NO', 'NO']], c.ui19.contributor)}</select></label>
        <label>If non-contributor, reason <select data-k="u_non_contributor_reason" ${dis}>${opts(NON_CONTRIB, c.ui19.non_contributor_reason)}</select></label>
      </div>

      <h3>Classes in this cohort</h3>
      <div class="ds-classes">${cls.length ? cls.map((x) => `<label><input type="checkbox" data-class="${x.class_id}" ${dis}> ${esc(x.class_name)}${x.is_test ? ' (test)' : ''}
        <span class="ds-hint">${esc(x.programme)}${x.muni ? ' · ' + esc(x.muni) : ''} · ${day(x.start_date)} – ${x.end_date ? day(x.end_date) : 'no end date'}${x.cohort_set ? '' : ' · default'}</span></label>`).join('') : '<span class="ds-hint">No classes yet.</span>'}</div>
      ${edit && cls.length ? `<div style="display:flex;gap:8px;align-items:center;margin-top:8px;flex-wrap:wrap">
        <span class="ds-hint">Move ticked classes to</span>
        <select data-k="move_to">${S.cohorts.filter((o) => o.cohort_id !== k.cohort_id).map((o) => `<option value="${o.cohort_id}">${esc(o.name)}</option>`).join('')}</select>
        <button type="button" class="btn btn-ghost" data-act="move">Move</button></div>` : ''}

      <details class="ds-adv">
        <summary>Advanced <span class="ds-hint">: where each value comes from, new-month values, system rules, fallback dates</span></summary>
        <h3>Where each value comes from</h3>
        <div class="ds-grid">
          <label>Termination reason <select data-k="src_period" ${dis}>${opts(SOURCES, c.sources.period)}</select>
            <span class="ds-hint">System: 6 · Resigned if they left early, else 5 · Contract expired</span></label>
          <label>Salary &amp; UIF <select data-k="src_amounts" ${dis}>${opts(SOURCES, c.sources.amounts)}</select>
            <span class="ds-hint">System: the learner's payslips</span></label>
          <label>Hours <select data-k="src_hours" ${dis}>${opts(SOURCES, c.sources.hours)}</select>
            <span class="ds-hint">System: the hours rule below</span></label>
        </div>
        <h3>New month values</h3>
        <p class="ds-hint" style="margin:0 0 8px">Used for a month added to the schedule (e.g. after a class's dates change).</p>
        <div class="ds-grid">
          <label>Salary (R) <input type="number" step="0.01" min="0" data-k="def_salary" value="${esc(c.month_default.salary)}" ${dis}></label>
          <label>Hours <input type="number" step="0.5" min="0" data-k="def_hours" value="${esc(c.month_default.hours)}" ${dis}></label>
          <label>UIF (R) <input type="number" step="0.01" min="0" data-k="def_uif" value="${esc(c.month_default.uif)}" ${dis}></label>
          <label>Contributor <input data-k="def_contributor" value="${esc(c.month_default.contributor)}" placeholder="blank, Contributor, …" ${dis}></label>
        </div>
        <div style="margin-top:8px"><button type="button" class="btn btn-ghost" data-act="reset-months" ${dis}>Reset every month to the new-month values</button></div>
        <h3>System rules <span class="ds-hint">(only used where a source is "From the system")</span></h3>
        <div class="ds-grid">
          <label>Salary amount <select data-k="sys_amount_mode" ${dis}>${opts(AMOUNT_MODES, c.system.amount_mode)}</select></label>
          <label>Hours rule <select data-k="sys_hours_mode" ${dis}>${opts(HOURS_MODES, c.system.hours_mode)}</select></label>
          <label>Hours value <input type="number" step="0.5" min="0" data-k="sys_hours_value" value="${esc(c.system.hours_value)}" ${dis}>
            <span class="ds-hint">Hours per paid month, or per day present</span></label>
        </div>
        <h3>Fallback dates</h3>
        <div class="ds-grid">
          <label>Fallback start <input type="date" data-k="period_start" value="${esc(k.period_start || '')}" ${dis}>
            <span class="ds-hint">Only for a class with no dates</span></label>
          <label>Fallback end <input type="date" data-k="period_end" value="${esc(k.period_end || '')}" ${dis}>
            <span class="ds-hint">Blank: up to the day printed</span></label>
        </div>
      </details>

      <div class="ds-actions">
        ${edit ? `<button type="button" class="btn btn-primary" data-act="save">Save ${esc(k.name)}</button>` : ''}
        <button type="button" class="btn btn-ghost" data-act="preview" title="Uses the ID number typed in &quot;Preview a learner's documents&quot; above">Preview with these values</button>
        ${k.updated_at ? `<span class="ds-hint">Last saved ${esc(new Date(k.updated_at).toLocaleString('en-ZA'))}</span>` : ''}
      </div>
      <p class="ds-msg" data-k="msg"></p>
    </div>
  </details>`;
}

function wireCard(root, k, edit, el) {
  const $ = (key) => root.querySelector(`[data-k="${key}"]`);
  const msg = (text, cls = '') => { const m = $('msg'); m.textContent = text; m.className = `ds-msg ${cls}`; };
  let schedule = withDefaults(k.config).schedule.map((r) => ({ ...r, month: String(r.month).slice(0, 10) }));

  const monthDefault = () => ({
    salary: Number($('def_salary').value || 0), hours: Number($('def_hours').value || 0),
    uif: Number($('def_uif').value || 0), contributor: $('def_contributor').value.trim(),
  });

  /* One row per month the cohort's classes cover; keep typed values for months that stay. */
  function syncRows(reset = false) {
    const keep = new Map(readRows().map((r) => [r.month, r]));
    const def = monthDefault();
    const span = cohortSpan(k, $('period_start').value || null, $('period_end').value || null);
    schedule = monthsBetween(span.start, span.end || todayIso()).map((month) =>
      (!reset && keep.get(month)) || (!reset && schedule.find((r) => r.month === month)) || { month, ...def });
    drawRows();
  }

  function drawRows() {
    const dis = edit ? '' : 'disabled';
    const amountsSys = $('src_amounts').value === 'system';
    const hoursSys = $('src_hours').value === 'system';
    $('schedule').innerHTML = schedule.map((r) => `
      <tr data-month="${esc(r.month)}">
        <td>${esc(fmtShort(r.month))}</td>
        <td><input type="number" step="0.01" min="0" data-f="salary" value="${esc(r.salary)}" ${amountsSys ? 'disabled' : dis}></td>
        <td><input type="number" step="0.5" min="0" data-f="hours" value="${esc(r.hours)}" ${hoursSys ? 'disabled' : dis}></td>
        <td><input type="number" step="0.01" min="0" data-f="uif" value="${esc(r.uif)}" ${amountsSys ? 'disabled' : dis}></td>
        <td><input data-f="contributor" value="${esc(r.contributor)}" ${amountsSys ? 'disabled' : dis}></td>
      </tr>`).join('') || `<tr><td colspan="5" class="ds-hint">No months yet: the cohort has no classes with dates and no fallback period.</td></tr>`;
    $('schedule').querySelectorAll('input').forEach((i) => i.addEventListener('input', showSum));
    showSum();
  }

  function readRows() {
    return [...$('schedule').querySelectorAll('tr[data-month]')].map((tr) => {
      const v = (f) => tr.querySelector(`[data-f="${f}"]`).value;
      return { month: tr.dataset.month, salary: Number(v('salary') || 0), hours: Number(v('hours') || 0), uif: Number(v('uif') || 0), contributor: v('contributor').trim() };
    });
  }

  function showSum() {
    const sum = readRows().reduce((a, r) => a + r.hours, 0);
    $('hours_sum').textContent = `Months above add up to ${sum}`;
  }

  function showNotes() {
    for (const key of ['amounts', 'hours']) root.querySelector(`[data-note="${key}"]`).hidden = $(`src_${key}`).value !== 'system';
    $('u_termination_code').disabled = !edit || $('src_period').value === 'system';
    $('u_remuneration').disabled = !edit || $('src_amounts').value === 'system';
    $('u_total_hours').disabled = !edit || $('src_hours').value === 'system';
  }

  /** The cohort row as currently typed (saved or not). */
  function readCohort() {
    const { send_with_payslips, ...config } = k.config || {};   // retired: WhatsApp sends what the sender picks
    return {
      ...k,
      name: $('name').value.trim() || k.name,
      period_start: $('period_start').value || null,
      period_end: $('period_end').value || null,
      config: {
        ...config,
        issue_date: $('issue_date').value || null,
        memo_file: $('memo_file').value || null,
        sources: { period: $('src_period').value, amounts: $('src_amounts').value, hours: $('src_hours').value },
        month_default: monthDefault(),
        schedule: readRows(),
        ui19: {
          remuneration: Number($('u_remuneration').value || 0),
          total_hours: Number($('u_total_hours').value || 0),
          termination_code: $('u_termination_code').value,
          contributor: $('u_contributor').value,
          non_contributor_reason: $('u_non_contributor_reason').value,
        },
        system: {
          amount_mode: $('sys_amount_mode').value,
          hours_mode: $('sys_hours_mode').value,
          hours_value: Number($('sys_hours_value').value || 0),
        },
      },
    };
  }

  $('period_start').addEventListener('change', () => syncRows());
  $('period_end').addEventListener('change', () => syncRows());
  for (const key of ['src_period', 'src_amounts', 'src_hours']) $(key).addEventListener('change', () => { schedule = readRows(); drawRows(); showNotes(); });
  root.querySelector('[data-act="reset-months"]').addEventListener('click', () => syncRows(true));

  root.querySelector('[data-act="move"]')?.addEventListener('click', async () => {
    const ids = [...root.querySelectorAll('[data-class]:checked')].map((c) => c.dataset.class);
    if (!ids.length) { msg('Tick the classes to move.', 'err'); return; }
    const to = Number($('move_to').value);
    const { error } = await supabase.rpc('set_class_cohort', { p_class_ids: ids, p_cohort_id: to });
    if (error) { msg(error.message, 'err'); return; }
    await loadBase();
    draw(el);
  });

  root.querySelector('[data-act="save"]')?.addEventListener('click', async () => {
    const next = readCohort();
    if (next.period_start && next.period_end && next.period_end < next.period_start) {
      msg('The fallback end must be on or after the start.', 'err'); return;
    }
    msg('Saving…');
    const { data, error } = await supabase.from('cohorts')
      .update({ name: next.name, period_start: next.period_start, period_end: next.period_end, config: next.config })
      .eq('cohort_id', k.cohort_id).select('updated_at').single();
    if (error) { msg(`Could not save: ${error.message}`, 'err'); return; }
    Object.assign(k, next, { updated_at: data.updated_at });
    msg('Saved. Documents built from now on use these values.', 'ok');
  });

  root.querySelector('[data-act="preview"]').addEventListener('click', () => previewById(el, readCohort(), msg));

  if (!schedule.length) syncRows(); else drawRows();
  showNotes();
}

/* ═══════════════════════════ Period corrections ═══════════════════════════ */

async function drawCorrections(box, edit) {
  const { data, error } = await supabase.from('learner_doc_overrides')
    .select('student_uuid,period_start,period_end,cohort_id,note,students(first_name,surname,national_id)');
  if (error) { box.innerHTML = `<p class="ds-msg err">${esc(error.message)}</p>`; return; }
  const rows = (data || []).sort((a, b) => (a.students?.surname || '').localeCompare(b.students?.surname || ''));
  const cohortName = (id) => S.cohorts.find((k) => k.cohort_id === id)?.name || '';
  box.innerHTML = `
    ${rows.length ? `<div class="ds-wrap"><table class="ds-table" style="margin-bottom:12px">
      <thead><tr><th>Learner</th><th>ID number</th><th>Start</th><th>End</th><th>Cohort</th><th>Why</th><th></th></tr></thead>
      <tbody>${rows.map((r) => `<tr>
        <td>${esc([r.students?.first_name, r.students?.surname].filter(Boolean).join(' '))}</td>
        <td>${esc(r.students?.national_id || '')}</td>
        <td>${r.period_start ? day(r.period_start) : '<span class="ds-hint">from class</span>'}</td>
        <td>${r.period_end ? day(r.period_end) : '<span class="ds-hint">from class</span>'}</td>
        <td>${r.cohort_id ? esc(cohortName(r.cohort_id)) : '<span class="ds-hint">from class</span>'}</td>
        <td>${esc(r.note || '')}</td>
        <td>${edit ? `<button class="btn btn-ghost" style="font-size:.76rem;padding:4px 10px" data-undo="${r.student_uuid}">Remove</button>` : ''}</td>
      </tr>`).join('')}</tbody></table></div>` : `<p class="ds-hint" style="margin:0 0 12px">None: every learner's documents follow their class and their own dates.</p>`}
    ${edit ? `<div class="ds-grid" style="align-items:end">
      <label style="display:flex;flex-direction:column;gap:3px;font-size:.74rem;font-weight:600;color:var(--text-muted)">Learner ID number <input data-c="id" inputmode="numeric" class="ds-in"></label>
      <label style="display:flex;flex-direction:column;gap:3px;font-size:.74rem;font-weight:600;color:var(--text-muted)">Start <input type="date" data-c="start" class="ds-in"></label>
      <label style="display:flex;flex-direction:column;gap:3px;font-size:.74rem;font-weight:600;color:var(--text-muted)">End <input type="date" data-c="end" class="ds-in"></label>
      <label style="display:flex;flex-direction:column;gap:3px;font-size:.74rem;font-weight:600;color:var(--text-muted)">Cohort <select data-c="cohort" class="ds-in"><option value="">From the class</option>${S.cohorts.map((k) => `<option value="${k.cohort_id}">${esc(k.name)}</option>`).join('')}</select></label>
      <label style="display:flex;flex-direction:column;gap:3px;font-size:.74rem;font-weight:600;color:var(--text-muted)">Why <input data-c="note" placeholder="optional" class="ds-in"></label>
      <button class="btn btn-ghost" data-c="add">+ Add correction</button>
    </div>
    <p class="ds-hint" style="margin:6px 0 0">Leave a date blank to keep the one from the class.</p>` : ''}
    <p class="ds-msg" data-c="msg"></p>`;
  box.querySelectorAll('.ds-in').forEach((i) => { i.style.cssText = 'padding:7px 9px;border:1px solid var(--border);border-radius:6px;background:var(--surface);color:var(--text);font:inherit;font-size:.85rem'; });

  const msg = (t, cls = '') => { const m = box.querySelector('[data-c="msg"]'); m.textContent = t; m.className = `ds-msg ${cls}`; };
  box.querySelectorAll('[data-undo]').forEach((b) => b.addEventListener('click', async () => {
    if (!confirm('Remove this correction? The documents go back to the class dates and cohort.')) return;
    const { error: e } = await supabase.from('learner_doc_overrides').delete().eq('student_uuid', b.dataset.undo);
    if (e) { msg(e.message, 'err'); return; }
    drawCorrections(box, edit);
  }));
  box.querySelector('[data-c="add"]')?.addEventListener('click', async () => {
    const v = (k) => box.querySelector(`[data-c="${k}"]`).value.trim();
    const id = v('id').replace(/\s+/g, '');
    const start = v('start') || null, end = v('end') || null, cohort = v('cohort') ? Number(v('cohort')) : null;
    if (!id) { msg('Type the learner\'s ID number.', 'err'); return; }
    if (!start && !end && !cohort) { msg('Set a date or a cohort.', 'err'); return; }
    if (start && end && end < start) { msg('The end must be on or after the start.', 'err'); return; }
    const { data: found, error: fe } = await supabase.from('students').select('student_uuid').eq('national_id', id).limit(2);
    if (fe) { msg(fe.message, 'err'); return; }
    if (!found?.length) { msg(`No learner has ID number ${id}.`, 'err'); return; }
    if (found.length > 1) { msg(`More than one learner has ID number ${id}; fix the duplicate first.`, 'err'); return; }
    const { error: ue } = await supabase.from('learner_doc_overrides').upsert(
      { student_uuid: found[0].student_uuid, period_start: start, period_end: end, cohort_id: cohort, note: v('note') || null },
      { onConflict: 'student_uuid' });
    if (ue) { msg(ue.message, 'err'); return; }
    drawCorrections(box, edit);
  });
}
